/**
 * list-actions — KATALOG akcji list (silnik akcji zbiorczych, PHASE 25 Faza 1).
 *
 * JEDNO źródło prawdy o tym, jakie akcje w ogóle istnieją, dla jakich kartotek są
 * dostępne, jaką bramką modułu / uprawnieniem są chronione i jak się wykonują.
 * Współdzielone przez:
 *  - apps/api  — endpoint dostępności (przecięcie z aktywnymi modułami + uprawnieniami)
 *                oraz twarda walidacja przy wykonaniu,
 *  - apps/admin — paleta w Kreatorze Listy (zakładka „Akcje") + wspólne menu akcji
 *                (dropdown nad listą / prawy klik / kebab w wierszu).
 *
 * Model dwuwarstwowy (jak pola formularza):
 *  - KATALOG (ten plik, w kodzie) = co dana kartoteka umie.
 *  - WYBÓR per layout (`ListConfig.listActions`, patrz `list-view.ts`) = co pokazać w tym widoku.
 *
 * Faza 1 = akcje na GOTOWYCH endpointach (rozgłoszenie po zaznaczonych ID). Silnik
 * wykonania akcji serwerowych i własne akcje tenanta (SQL/webhook) — Faza 2.
 *
 * Semantyka „usuń" vs „archiwizuj" (decyzja właścicielki 2026-09-10):
 *  - `delete` = USUŃ = miękkie usunięcie (`gcrecord`) — JEDYNA forma kasowania, wszędzie.
 *    Twarde kasowanie NIE ISTNIEJE (żaden rekord nie znika z bazy). Wyjątek: firm nie da
 *    się usuwać (`excludedKartoteki`).
 *  - `archive`/`unarchive` = ARCHIWIZUJ / przywróć z archiwum = osobna flaga boolean,
 *    TYLKO dokumenty (endpointy dochodzą osobno; patrz PHASE_25_PLAN „ujednolicenie").
 */

import { z } from 'zod';

import { type RecordCategoryKartotekaKey } from './record-categories';

// ── Identyfikatory wbudowanych akcji (Faza 1) ────────────────────────────────

export const LIST_ACTION_IDS = [
  'delete', // USUŃ = miękkie usunięcie (gcrecord) — JEDYNA forma kasowania, wszędzie.
  //          Twarde kasowanie NIE ISTNIEJE (żaden rekord nie znika z bazy — decyzja właścicielki).
  'archive', // ARCHIWIZUJ — flaga boolean, TYLKO dokumenty (osobne od usuwania).
  'unarchive', // Przywróć z archiwum — zdejmij flagę, TYLKO dokumenty.
  'export_csv', // „Eksport zaznaczonych do CSV" — widoczne kolumny widoku, tylko zaznaczone rekordy
  'export_list_csv', // „Eksport listy do CSV" — cały przefiltrowany widok (E16), bez zaznaczania
  'export_excel',
  'import', // otwiera modal importu (ogólne, niezależne od zaznaczenia)
  'refresh', // odświeżenie listy (ogólne)
  'bulk_change', // „Zmień zbiorczo" — akcja jednorazowa: N zmian (pole/kategoria/grupa) naraz
  //               na zaznaczonych, w jednej transakcji, BEZ zapisu definicji (PHASE 25 Faza 2 §8)
  'ksef_send', // wyślij do KSeF (faktury sprzedaży, moduł ksef_integration)
  'fk_send', // wyślij do FK/Symfonia (dokumenty, moduł symfonia_integration)
  'decree', // dekretuj wsadowo wg procedury/szablonu (faktury; modal → wsadowy zapis)
] as const;

export type ListActionId = (typeof LIST_ACTION_IDS)[number];

export function isListActionId(id: string): id is ListActionId {
  return (LIST_ACTION_IDS as readonly string[]).includes(id);
}

// ── Sposób wykonania (interpretuje FRONTEND) ─────────────────────────────────
//
// Faza 1 nie ma wsadowego backendu — akcje jadą albo rozgłoszeniem po ID na
// istniejące per-rekordowe endpointy (`fanout`), albo jednym wywołaniem gdy
// endpoint zbiorczy już istnieje (`bulk` / `download`), albo czysto po froncie
// (`client`). Szablony ścieżek zawierają `:entity` (baza listy — podstawia
// komponent listy) i/lub `:id` (per rekord).

export type ListActionDispatch =
  | { kind: 'fanout'; method: 'DELETE' | 'POST'; path: string }
  | { kind: 'bulk'; method: 'POST'; path: string; idsKey: string }
  | { kind: 'download'; method: 'POST'; path: string; idsKey: string }
  | {
      kind: 'client';
      op: 'refresh' | 'open_import' | 'open_decree' | 'open_bulk_change' | 'export_list';
    };

// ── Zakres kartotek, których dotyczy akcja ───────────────────────────────────
//
// `'all'` = każda kartoteka (akcje generyczne: archiwizuj/przywróć/eksport/…).
// Lista kluczy = tylko wskazane kartoteki. `documentClasses` dodatkowo zawęża
// akcję w obrębie kartoteki `documents` do konkretnych klas dokumentu (np. KSeF
// tylko dla faktur sprzedaży).

export type ListActionScope = 'all' | readonly ListActionKartotekaKey[];

/**
 * Klucz kartoteki dla SILNIKA AKCJI. Nadzbiór kluczy osi kategorii
 * (`RecordCategoryKartotekaKey`) o dodatkowy `'generic'` — listy spoza tej osi
 * (moduł własny tenanta / moduł systemowy z Kreatora, runtime `ModuleRecordsPage`).
 * `'generic'` z definicji łapie tylko akcje uniwersalne (`appliesTo: 'all'`):
 * akcje modułowe (KSeF/FK) mają `appliesTo: ['documents']`, więc nigdy nie pasują
 * do `'generic'` — dokładnie taki jest cel (każdy moduł dostaje archiwizuj/przywróć/
 * eksport/import/odśwież, ZERO KSeF/FK).
 *
 * NIE mieszać z `RecordCategoryKartotekaKey` (oś kategorii, allowlista zapisu w
 * backendzie) — `'generic'` tam nie należy i nie może się przeciec do tej domeny.
 */
export const GENERIC_LIST_KARTOTEKA_KEY = 'generic' as const;

export type ListActionKartotekaKey = RecordCategoryKartotekaKey | typeof GENERIC_LIST_KARTOTEKA_KEY;

export interface ListActionDef {
  /** Stały identyfikator akcji. */
  readonly id: ListActionId;
  /** Klucz i18n etykiety w menu / palecie. */
  readonly labelKey: string;
  /** Nazwa ikony (Tabler/lucide) — dobiera front. */
  readonly icon: string;
  /**
   * Czy akcja działa NA ZAZNACZONYCH rekordach (`true` → sekcja „Na zaznaczonych",
   * aktywna dopiero po zaznaczeniu) czy jest OGÓLNA (`false` → sekcja „Ogólne",
   * zawsze aktywna, np. import/odśwież).
   */
  readonly requiresSelection: boolean;
  /** Akcja niszcząca (czerwony wariant + wymagane potwierdzenie). */
  readonly destructive?: boolean;
  /** Dla jakich kartotek dostępna. */
  readonly appliesTo: ListActionScope;
  /**
   * Kartoteki WYKLUCZONE z akcji mimo `appliesTo` (wyjątki od „wszędzie"). Np. firm
   * NIE można usuwać w apce (`delete` wykluczony dla `companies`) — decyzja właścicielki.
   */
  readonly excludedKartoteki?: readonly RecordCategoryKartotekaKey[];
  /** Zawężenie do klas dokumentu (tylko dla kartoteki `documents`). */
  readonly documentClasses?: readonly string[];
  /**
   * Zawężenie klas WEWNĄTRZ kartoteki `documents`, gdy akcja dotyczy też innych kartotek (np.
   * eksport widoku listy). W odróżnieniu od `documentClasses` nie zamyka akcji na same dokumenty —
   * dla pozostałych kartotek z `appliesTo` jest bez znaczenia.
   */
  readonly documentClassFilter?: readonly string[];
  /** Kod modułu wymagany do dostępności akcji (bramka billingowa). */
  readonly moduleCode?: string;
  /** Kod uprawnienia wymagany do wykonania akcji. */
  readonly permission?: string;
  /**
   * Alternatywa dla `permission`: WYSTARCZY jeden z tych kodów.
   *
   * Potrzebne tam, gdzie jedna akcja obsługuje kilka klas dokumentu o ROZŁĄCZNYCH kodach —
   * dekretacja i wysyłka do księgowości dotyczą faktur zakupu i sprzedaży, a te mają od F24
   * osobne uprawnienia. Pojedynczy kod odciąłby połowę uprawnionych.
   */
  readonly permissionAnyOf?: readonly string[];
  /** Jak akcję wykonać. */
  readonly dispatch: ListActionDispatch;
}

// ── Klasy dokumentów objęte wysyłką (jedno źródło prawdy) ────────────────────
//
// Używane W DWÓCH miejscach: `documentClasses` w katalogu (bramka „gdzie akcja w
// ogóle dotyczy") ORAZ `defaultListActionIdsFor` (czy jest w zestawie domyślnym).
// Trzymamy je razem, żeby zmiana zakresu klas (np. korekty) nie rozjechała obu.

/** Klasy dokumentu, dla których dostępna jest wysyłka do KSeF. */
export const KSEF_DOCUMENT_CLASSES: readonly string[] = ['sale_invoice'];
/** Klasy dokumentu, dla których dostępna jest wysyłka do FK/Symfonia. */
export const FK_DOCUMENT_CLASSES: readonly string[] = ['sale_invoice', 'purchase_invoice'];
/**
 * Klasy dokumentu z dekretacją (zakładka Dekretacja + procedury/szablony dekretu).
 * Dziś tylko obie klasy faktur — jedyne z write-path `document_decree_lines`.
 */
export const DECREE_DOCUMENT_CLASSES: readonly string[] = ['sale_invoice', 'purchase_invoice'];

// ── Eksport widoku listy do CSV (E16) — gdzie działa ─────────────────────────
//
// Akcja „Eksport listy do CSV" pojawia się tylko tam, gdzie strona listy podpina eksport (trasa
// `…/export` + wspólny hook `useEngineListExport` / `useListCsvExport`). Podpinając kolejną
// kartotekę, dopisz ją TUTAJ (a klasę dokumentu — w `LIST_EXPORT_DOCUMENT_CLASSES` i klucz
// `documents` w kartotekach). Jedno miejsce: zakres w katalogu i zestaw domyślny czytają te stałe.

/** Kartoteki z eksportem widoku listy (`generic` = listy modułów własnych/kreatorowych). */
export const LIST_EXPORT_KARTOTEKI: readonly ListActionKartotekaKey[] = [
  'contractors',
  'employees',
  'companies',
  'users',
  'products',
  'documents',
  GENERIC_LIST_KARTOTEKA_KEY,
];

/** Klasy dokumentów z eksportem widoku listy (gdy `documents` jest w `LIST_EXPORT_KARTOTEKI`). */
export const LIST_EXPORT_DOCUMENT_CLASSES: readonly string[] = [
  'purchase_invoice',
  'sale_invoice',
  'contract',
  'letter',
  'commercial',
  'internal',
];

// ── Katalog wbudowanych akcji ────────────────────────────────────────────────

export const LIST_ACTION_CATALOG: Readonly<Record<ListActionId, ListActionDef>> = {
  delete: {
    id: 'delete',
    labelKey: 'tenant.listActions.delete',
    icon: 'trash',
    requiresSelection: true,
    // USUŃ = miękkie usunięcie (gcrecord). Odwracalne w bazie, ale w UI jednokierunkowe
    // (na razie brak widoku „kosz"/odzyskaj — dojdzie z centrum uprawnień). Wymaga
    // potwierdzenia. NIE ma twardego kasowania — `path` bez `?hard=true`.
    appliesTo: 'all',
    // Firm NIE można usuwać w apce (decyzja właścicielki) — wyjątek od „wszędzie".
    excludedKartoteki: ['companies'],
    dispatch: { kind: 'fanout', method: 'DELETE', path: '/:entity/:id' },
  },
  archive: {
    id: 'archive',
    labelKey: 'tenant.listActions.archive',
    icon: 'archive',
    requiresSelection: true,
    // ARCHIWIZUJ = flaga boolean na dokumencie (osobne od usuwania). TYLKO dokumenty.
    appliesTo: ['documents'],
    dispatch: { kind: 'fanout', method: 'POST', path: '/documents/:id/archive' },
  },
  unarchive: {
    id: 'unarchive',
    labelKey: 'tenant.listActions.unarchive',
    icon: 'restore',
    requiresSelection: true,
    // Przywróć z archiwum = zdejmij flagę. TYLKO dokumenty.
    appliesTo: ['documents'],
    dispatch: { kind: 'fanout', method: 'POST', path: '/documents/:id/unarchive' },
  },
  export_csv: {
    id: 'export_csv',
    labelKey: 'tenant.listActions.exportCsv',
    icon: 'file-export',
    requiresSelection: true,
    appliesTo: 'all',
    dispatch: { kind: 'download', method: 'POST', path: '/:entity/export', idsKey: 'ids' },
  },
  export_list_csv: {
    id: 'export_list_csv',
    labelKey: 'tenant.listActions.exportListCsv',
    icon: 'file-export',
    requiresSelection: false,
    // Eksport WIDOKU listy działa tam, gdzie strona podpina handler — pozostałe kartoteki go
    // nie dostają, żeby w menu nie było „wkrótce". Zakres: stałe `LIST_EXPORT_*` wyżej.
    appliesTo: LIST_EXPORT_KARTOTEKI,
    documentClassFilter: LIST_EXPORT_DOCUMENT_CLASSES,
    // Wykonuje strona (handler `export_list_csv`): te same filtry co lista, wszystkie strony,
    // tylko widoczne kolumny — serwer nie zna etykiet z ekranu, więc plik składa przeglądarka.
    dispatch: { kind: 'client', op: 'export_list' },
  },
  export_excel: {
    id: 'export_excel',
    labelKey: 'tenant.listActions.exportExcel',
    icon: 'file-spreadsheet',
    requiresSelection: true,
    appliesTo: 'all',
    dispatch: {
      kind: 'download',
      method: 'POST',
      path: '/:entity/export?format=xlsx',
      idsKey: 'ids',
    },
  },
  import: {
    id: 'import',
    labelKey: 'tenant.listActions.import',
    icon: 'upload',
    requiresSelection: false,
    appliesTo: 'all',
    dispatch: { kind: 'client', op: 'open_import' },
  },
  refresh: {
    id: 'refresh',
    labelKey: 'tenant.listActions.refresh',
    icon: 'refresh',
    requiresSelection: false,
    appliesTo: 'all',
    dispatch: { kind: 'client', op: 'refresh' },
  },
  bulk_change: {
    id: 'bulk_change',
    labelKey: 'tenant.listActions.bulkChange.label',
    icon: 'edit',
    requiresSelection: true,
    // Akcja jednorazowa: otwiera modal z listą zmian (pole/kategoria/grupa) i woła wsadowy
    // endpoint (`/list-actions/bulk-change`), który wykonuje wszystko w JEDNEJ transakcji.
    // Bez zapisu definicji. Bramka WYKONANIA (super-user tenanta) siedzi na endpoincie —
    // tu tylko dostępność. Modal wbudowany w `useListActions` (działa na każdej liście bez
    // handlera strony), dlatego dispatch kliencki, nie `NEEDS_HANDLER`.
    appliesTo: 'all',
    dispatch: { kind: 'client', op: 'open_bulk_change' },
  },
  ksef_send: {
    id: 'ksef_send',
    labelKey: 'tenant.listActions.ksefSend',
    icon: 'send',
    requiresSelection: true,
    appliesTo: ['documents'],
    documentClasses: KSEF_DOCUMENT_CLASSES,
    moduleCode: 'ksef_integration',
    permission: 'ksef.send.submit',
    dispatch: { kind: 'fanout', method: 'POST', path: '/sale-invoices/:id/send-to-ksef' },
  },
  fk_send: {
    id: 'fk_send',
    labelKey: 'tenant.listActions.fkSend',
    icon: 'building-bank',
    requiresSelection: true,
    appliesTo: ['documents'],
    documentClasses: FK_DOCUMENT_CLASSES,
    moduleCode: 'symfonia_integration',
    permissionAnyOf: [
      'documents.purchase_invoice.manage_accounting',
      'documents.sale_invoice.manage_accounting',
    ],
    dispatch: { kind: 'fanout', method: 'POST', path: '/documents/:id/send-to-symfonia' },
  },
  decree: {
    id: 'decree',
    labelKey: 'tenant.listActions.decree',
    icon: 'calculator',
    requiresSelection: true,
    appliesTo: ['documents'],
    documentClasses: DECREE_DOCUMENT_CLASSES,
    // Bez `moduleCode` — dekretacja to część pakietu bazowego (crm_core), nie osobny
    // moduł billingowy. Bramka to tylko uprawnienie do księgowania (jak przy edycji
    // siatki Wn/Ma i „Podpowiedz dekret" na pojedynczej fakturze).
    permissionAnyOf: [
      'documents.purchase_invoice.manage_accounting',
      'documents.sale_invoice.manage_accounting',
    ],
    // Modal wsadowy: strona faktur podaje handler `decree` (jak import), który otwiera
    // okno wyboru procedury/szablonu i woła wsadowy endpoint. Bez handlera akcja świeci
    // „wkrótce" (NEEDS_HANDLER w useListActions) — nigdy nie wykonuje się generykiem.
    dispatch: { kind: 'client', op: 'open_decree' },
  },
};

/** Katalog jako lista (stabilna kolejność = kolejność w menu domyślnie). */
export const LIST_ACTION_LIST: readonly ListActionDef[] = LIST_ACTION_IDS.map(
  (id) => LIST_ACTION_CATALOG[id],
);

/**
 * Akcje domyślne — włączane dla KAŻDEJ kartoteki gdy layout nie ma własnej
 * konfiguracji. Uniwersalne, bezpieczne: „Usuń" (miękkie, gcrecord) + eksport CSV +
 * ogólne import/odśwież. Archiwum (archive/unarchive) jest domyślne TYLKO na
 * dokumentach, a KSeF/FK tylko na fakturach — patrz `defaultListActionIdsFor`.
 */
export const DEFAULT_LIST_ACTION_IDS: readonly ListActionId[] = [
  'delete',
  'bulk_change',
  'export_csv',
  'import',
  'refresh',
];

/**
 * Domyślny zestaw akcji dla KONKRETNEJ kartoteki (gdy layout nie ma własnej
 * konfiguracji `listActions`). Bazowo `DEFAULT_LIST_ACTION_IDS`; na DOKUMENTACH
 * dokładamy archiwum (archive/unarchive), a na FAKTURACH dodatkowo wysyłkę do systemów
 * zewnętrznych (faktura sprzedaży: KSeF + FK/Symfonia, faktura zakupu: FK/Symfonia) ORAZ
 * dekretację wsadową na OBU klasach faktur (decyzja właścicielki: dekret to codzienna
 * czynność księgowej, ma być pod ręką bez wchodzenia w Kreator). Wszystko i tak
 * przechodzi bramkę modułu + uprawnienia w resolverze — akcje pojawią się tylko gdzie
 * mają prawo.
 *
 * Archiwum (archive/unarchive) jest domyślne TYLKO na dokumentach — endpointy
 * `/documents/:id/archive|unarchive` + kolumna `documents.archived_at` (migracja 0160,
 * PHASE 25). Kartoteki systemowe/customowe archiwum NIE mają (tylko „Usuń" = gcrecord).
 */
export function defaultListActionIdsFor(
  kartotekaKey: ListActionKartotekaKey,
  documentClass?: string,
): readonly ListActionId[] {
  // Eksport widoku listy: tylko tam, gdzie strona go obsługuje (`LIST_EXPORT_*`) — zaraz za
  // „Eksport zaznaczonych", żeby dwie akcje eksportu stały obok siebie w menu.
  const base: readonly ListActionId[] = isActionApplicable(
    LIST_ACTION_CATALOG.export_list_csv,
    kartotekaKey,
    documentClass,
  )
    ? DEFAULT_LIST_ACTION_IDS.flatMap((id) =>
        id === 'export_csv' ? [id, 'export_list_csv' as const] : [id],
      )
    : DEFAULT_LIST_ACTION_IDS;
  if (kartotekaKey !== 'documents' || !documentClass) return base;
  // Archiwum dokumentów — wspólne dla wszystkich 7 klas (kartoteka „Dokument").
  const extra: ListActionId[] = ['archive', 'unarchive'];
  if (KSEF_DOCUMENT_CLASSES.includes(documentClass)) extra.push('ksef_send');
  if (FK_DOCUMENT_CLASSES.includes(documentClass)) extra.push('fk_send');
  if (DECREE_DOCUMENT_CLASSES.includes(documentClass)) extra.push('decree');
  return [...base, ...extra];
}

// ── Dostępność akcji dla kartoteki ───────────────────────────────────────────

/**
 * Czy akcja z katalogu w ogóle DOTYCZY danej kartoteki (bez sprawdzania modułu i
 * uprawnień — to warstwa runtime). `documentClass` opcjonalny: dla listy klasy
 * dokumentu zawęża akcje z `documentClasses` do pasujących.
 */
export function isActionApplicable(
  def: ListActionDef,
  kartotekaKey: ListActionKartotekaKey,
  documentClass?: string,
): boolean {
  // `appliesTo` to `'all'` albo lista kluczy kartotek. Klucz `'generic'` (listy modułów) pasuje
  // tylko do akcji `'all'` albo do listy, która go jawnie wymienia (np. eksport widoku listy).
  const scopeOk = def.appliesTo === 'all' || def.appliesTo.includes(kartotekaKey);
  if (!scopeOk) return false;
  // Wyjątki od „wszędzie" (np. firm nie da się usuwać).
  if (
    def.excludedKartoteki &&
    (def.excludedKartoteki as readonly string[]).includes(kartotekaKey)
  ) {
    return false;
  }
  // Zawężenie klas w obrębie dokumentów (akcja dotyczy też innych kartotek — patrz pole).
  if (kartotekaKey === 'documents' && def.documentClassFilter) {
    if (!documentClass || !def.documentClassFilter.includes(documentClass)) return false;
  }
  if (def.documentClasses) {
    if (kartotekaKey !== 'documents') return false;
    if (!documentClass) return false;
    return def.documentClasses.includes(documentClass);
  }
  return true;
}

/** Akcje z katalogu dotyczące danej kartoteki (paleta w Kreatorze). */
export function actionsForKartoteka(
  kartotekaKey: ListActionKartotekaKey,
  documentClass?: string,
): readonly ListActionDef[] {
  return LIST_ACTION_LIST.filter((def) => isActionApplicable(def, kartotekaKey, documentClass));
}

// ── Wybór akcji per layout (idzie do `ListConfig.listActions`) ────────────────

/**
 * Pojedynczy wpis „akcja włączona w tym widoku listy". Referencja do katalogu
 * (`actionId`) + kolejność. Metadane (etykieta, ikona, bramki) NIE są tu
 * duplikowane — czyta się je z katalogu po `actionId`.
 */
export const listActionConfigSchema = z.object({
  /** Identyfikator akcji z katalogu (`LIST_ACTION_IDS`). */
  actionId: z.enum(LIST_ACTION_IDS),
  /** Kolejność w menu (0-indexed). */
  order: z.number().int().nonnegative(),
});

export type ListActionConfig = z.infer<typeof listActionConfigSchema>;
